import { useEffect, useState } from 'react';
import { api, ApiError, type Pin } from '../api';

/**
 * Inloggen met de Plex-PIN-flow: code op het scherm, bevestigen op plex.tv/link.
 * Werkt hetzelfde op tv, desktop en telefoon.
 */
export function Login({ onLoggedIn, welcome }: { onLoggedIn: () => void; welcome?: string }) {
  const [mode, setMode] = useState<'plex' | 'email'>(() => (sessionStorage.getItem('tumedia.loginMode') === 'email' ? 'email' : 'plex'));
  if (mode === 'email') return <EmailLogin onLoggedIn={onLoggedIn} onPlex={() => { setMode('plex'); try { sessionStorage.removeItem('tumedia.loginMode'); } catch { /* */ } }} />;
  return <PlexLogin onLoggedIn={onLoggedIn} welcome={welcome} onEmail={() => setMode('email')} />;
}

/** Inloggen zonder Plex: e-mail en wachtwoord (accounts die via een uitnodiging zonder Plex zijn gemaakt). */
function EmailLogin({ onLoggedIn, onPlex }: { onLoggedIn: () => void; onPlex: () => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.login(email, password);
      onLoggedIn();
    } catch (err) {
      setError(err instanceof ApiError && err.message !== err.code ? err.message : 'Inloggen mislukt. Probeer het opnieuw.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <main className="center-screen ob-screen">
      <div className="ob-card">
        <div className="logo">tu<span>media</span></div>
        <h1 className="ob-title">Inloggen</h1>
        <form className="form-grid ob-form" onSubmit={(e) => { e.preventDefault(); void submit(); }}>
          <label>E-mailadres<input className="text-input" type="email" required autoFocus autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} /></label>
          <label>Wachtwoord<input className="text-input" type="password" required autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} /></label>
          {error && <p className="error" role="alert">{error}</p>}
          <button className="btn btn-primary" type="submit" disabled={busy}>{busy ? 'Bezig…' : 'Inloggen'}</button>
          <p className="subtle hint">Wachtwoord vergeten? Vraag de beheerder om een nieuw tijdelijk wachtwoord.</p>
        </form>
        <button type="button" className="linklike" onClick={onPlex}>Inloggen met Plex</button>
      </div>
    </main>
  );
}

function PlexLogin({ onLoggedIn, welcome, onEmail }: { onLoggedIn: () => void; welcome?: string; onEmail: () => void }) {
  const [pin, setPin] = useState<Pin | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let stopped = false;
    let timer: number | undefined;
    setError(null);
    setPin(null);

    api.createPin()
      .then((p) => {
        if (stopped) return;
        setPin(p);
        const expires = new Date(p.expiresAt).getTime();

        const poll = async () => {
          if (stopped) return;
          if (Date.now() > expires) {
            setAttempt((a) => a + 1); // nieuwe code
            return;
          }
          try {
            const res = await api.checkPin(p.id);
            if (res.status === 'ok') {
              onLoggedIn();
              return;
            }
          } catch (err) {
            if (err instanceof ApiError && err.status === 403) {
              setError(err.message || 'Dit Plex-account heeft geen toegang tot deze server.');
              return;
            }
            // netwerkhapering: gewoon blijven proberen
          }
          timer = window.setTimeout(poll, 2000);
        };
        timer = window.setTimeout(poll, 2000);
      })
      .catch(() => setError('Kan plex.tv niet bereiken. Probeer het zo opnieuw.'));

    return () => {
      stopped = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [attempt, onLoggedIn]);

  return (
    <main className="center-screen">
      <div className="logo">tu<span>media</span></div>
      {welcome && <p className="ob-welcome">Welkom {welcome}! Je aanmelding is ontvangen. Log nu in met je Plex-account.</p>}
      <h1 style={{ fontSize: 'calc(var(--u) * 3)' }}>Inloggen met Plex</h1>
      <p className="lead muted">
        Ga op je telefoon of computer naar <strong>plex.tv/link</strong> en voer deze code in.
      </p>
      <div className="pin-code" aria-live="polite" aria-label={pin ? `Code ${pin.code.split('').join(' ')}` : 'Code wordt opgehaald'}>
        {pin?.code ?? '····'}
      </div>
      {error ? (
        <p className="error lead" role="alert">{error}</p>
      ) : (
        <p className="subtle">Wachten op bevestiging…</p>
      )}
      <div style={{ display: 'flex', gap: 'calc(var(--u) * 1)', flexWrap: 'wrap', justifyContent: 'center' }}>
        <a className="btn btn-primary" href="https://plex.tv/link" target="_blank" rel="noreferrer" data-autofocus>
          plex.tv/link openen
        </a>
        <button className="btn" onClick={() => setAttempt((a) => a + 1)}>Nieuwe code</button>
      </div>
      <button type="button" className="linklike" style={{ marginTop: 'calc(var(--u) * 2)' }} onClick={onEmail}>Geen Plex? Inloggen met e-mail</button>
    </main>
  );
}
